<script setup lang="ts">
import { ref, onMounted } from 'vue';
import BreadCrumb from '@/components/general/BreadCrumb.vue';
import MainContentPanel from '@/components/display/general/MainContentPanel.vue';
import DataTable from 'primevue/datatable';
import type { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import { useState } from '@/composables/useVuexHelpers';
import { xhr } from '@/xhr/endpoints';
import _ from 'lodash';

interface User {
  _id: string;
  name: string;
  emails: string[];
}

interface LazyEvent {
  sortField?: string;
  sortOrder?: number;
  page?: number;
}

const defaultSortColumn = 'name';

const { idToken } = useState(['idToken']);

const users = ref<User[]>([]);
const loading = ref(false);
const resultsPerPage = ref(20);
const page = ref(0);
const sortColumn = ref(defaultSortColumn);
const sortDirection = ref('asc');
const totalRecords = ref(100);
const filters = ref({
  global: '',
});

const loadData = async () => {
  loading.value = true;
  try {
    const response = await xhr.getUsers(
      page.value,
      resultsPerPage.value,
      sortColumn.value,
      sortDirection.value,
      filters.value.global,
      idToken.value
    );
    users.value = response.results;
    totalRecords.value = response.totalCount;
  } catch (e) {
    console.log(e);
  } finally {
    loading.value = false;
  }
};

const onLazy = async (event: LazyEvent | Event | DataTablePageEvent | DataTableSortEvent) => {
  const lazyEvent = event as LazyEvent;
  console.log(lazyEvent);
  if (lazyEvent.sortField) {
    sortColumn.value = lazyEvent.sortField || sortColumn.value;
    sortDirection.value = lazyEvent.sortOrder && lazyEvent.sortOrder >= 0 ? 'asc' : 'desc';
  }
  if (typeof lazyEvent.page !== 'undefined' && lazyEvent.page !== null) {
    page.value = lazyEvent.page;
  }
  await loadData();
};

const debouncedFilter = _.debounce(async function () {
  await loadData();
}, 1000);

const rowClick = (event: unknown) => {
  console.log(event);
};

const getUserLink = (user: User) => {
  return `/#/user-admin/${user._id}`;
};

onMounted(async () => {
  await loadData();
});
</script>

<template>
  <div>
    <BreadCrumb
      :path="[
        {
          title: 'Users',
          route: '/users-admin',
        },
      ]"
    />
    <MainContentPanel title="Users" buttonIcon="users" padding="small">
      <DataTable
        :value="users"
        :rows="resultsPerPage"
        :lazy="true"
        :paginator="true"
        :totalRecords="totalRecords"
        :loading="loading"
        :filters="filters"
        @page="onLazy($event)"
        @sort="onLazy($event)"
        @row-click="rowClick($event)"
        :row-hover="true"
      >
        <template #header>
          <div class="table-header" style="text-align: right">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText
                v-model="filters['global']"
                placeholder="Global Search"
                @keyup="debouncedFilter()"
              />
            </span>
          </div>
        </template>
        <template #loading> Loading users. Please wait. </template>
        <Column field="name" header="Name" :sortable="true">
          <template #body="userProps">
            <a :href="getUserLink(userProps.data)">{{ userProps.data.name }}</a>
          </template>
        </Column>
        <Column header="Emails">
          <template #body="userProps">
            <a :href="getUserLink(userProps.data)">{{ userProps.data.emails.join(', ') }}</a>
          </template>
        </Column>
      </DataTable>
    </MainContentPanel>
  </div>
</template>
